import { useState } from 'react';
import { supabase, CREDIT_PACKS } from '../../lib/supabase';

interface BuyCreditsModalProps {
  onClose: () => void;
}

const SUPABASE_FUNCTIONS_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL + '/functions/v1';

const BuyCreditsModal = ({ onClose }: BuyCreditsModalProps) => {
  const [selected, setSelected] = useState<string>('pack_1000');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleBuy = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError('Please sign in to purchase credits.');
        return;
      }

      const successUrl = `${window.location.origin}/payment-success?session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${window.location.origin}/pricing`;

      const response = await fetch(`${SUPABASE_FUNCTIONS_URL}/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ pack_id: selected, success_url: successUrl, cancel_url: cancelUrl }),
      });

      const data = await response.json();
      if (!response.ok || !data.url) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      window.location.href = data.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Buy Credits</h2>
            <p className="text-sm text-gray-500 mt-0.5">One-time purchase · Credits never expire</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500"></i>
          </button>
        </div>

        <div className="p-6 space-y-3">
          {CREDIT_PACKS.map(pack => (
            <button
              key={pack.id}
              onClick={() => setSelected(pack.id)}
              className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all cursor-pointer ${
                selected === pack.id
                  ? 'border-gray-900 bg-gray-50'
                  : 'border-gray-100 hover:border-gray-200'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  selected === pack.id ? 'border-gray-900 bg-gray-900' : 'border-gray-300'
                }`}>
                  {selected === pack.id && (
                    <i className="ri-check-line text-white text-xs"></i>
                  )}
                </div>
                <div className="text-left">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900 text-sm">{pack.label}</span>
                    {pack.popular && (
                      <span className="text-xs bg-gray-900 text-white px-2 py-0.5 rounded-full">Popular</span>
                    )}
                    {pack.bestValue && (
                      <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded-full">Best Value</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    ${(pack.price / pack.credits * 1000).toFixed(2)} per 1K credits
                  </p>
                </div>
              </div>
              <span className="font-bold text-gray-900">${pack.price}</span>
            </button>
          ))}

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg text-sm text-red-600">
              <i className="ri-error-warning-line flex-shrink-0"></i>
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="px-6 pb-6">
          <button
            onClick={handleBuy}
            disabled={loading}
            className="w-full bg-gray-900 text-white font-semibold py-3 rounded-xl hover:bg-gray-800 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
          >
            {loading ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Redirecting to checkout...
              </>
            ) : (
              <>
                <i className="ri-secure-payment-line"></i>
                Pay with Stripe
              </>
            )}
          </button>
          <p className="text-xs text-gray-400 text-center mt-3">Powered by Stripe · Secure payment</p>
        </div>
      </div>
    </div>
  );
};

export default BuyCreditsModal;
